import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LocalDateTime } from "@/components/ui/local-datetime";
import { cn } from "@/lib/utils";

export type UpdateView = {
  id: string;
  message: string;
  visibleToCustomer: boolean;
  createdAt: string; // ISO string
};

export function UpdatesThread({
  orderId,
  initialUpdates,
}: {
  orderId: string;
  initialUpdates: UpdateView[];
}) {
  const [message, setMessage] = useState("");
  const [visibleToCustomer, setVisibleToCustomer] = useState(true);
  const [busy, setBusy] = useState(false);

  async function onPost(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/orders/${orderId}/updates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: trimmed,
          visibleToCustomer,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        toast.error(body.error ?? "Could not post update");
        return;
      }
      toast.success(
        visibleToCustomer
          ? "Update posted — customer notified."
          : "Internal note saved.",
      );
      setMessage("");
      window.location.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-4 p-5">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">Build updates</h3>
        <p className="text-xs text-muted-foreground">
          Customer-visible notes also email the customer. Internal notes are
          admin-only.
        </p>
      </div>

      <form onSubmit={onPost} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="update-message" className="sr-only">
            Update message
          </Label>
          <Textarea
            id="update-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What did the team get done?"
            rows={3}
            disabled={busy}
            required
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={visibleToCustomer}
              onCheckedChange={(v) => setVisibleToCustomer(v as boolean)}
              disabled={busy}
              id="visible-to-customer"
            />
            <span>
              Visible to customer{" "}
              <span className="text-xs text-muted-foreground">
                {visibleToCustomer
                  ? "(emails them)"
                  : "(internal note only)"}
              </span>
            </span>
          </label>
          <Button type="submit" disabled={busy || message.trim().length === 0}>
            {busy ? "Posting…" : "Post update"}
          </Button>
        </div>
      </form>

      <div className="border-t pt-4">
        {initialUpdates.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            No updates yet — be the first to post.
          </p>
        ) : (
          <ul className="space-y-3">
            {initialUpdates.map((u) => (
              <li
                key={u.id}
                className={cn(
                  "rounded-lg border p-3",
                  u.visibleToCustomer
                    ? "border-border"
                    : "border-amber-300/60 bg-amber-50/60",
                )}
              >
                <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>
                    <LocalDateTime iso={u.createdAt} preset="medium" />
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.06em]">
                    {u.visibleToCustomer ? (
                      <>
                        <Eye className="size-3" />
                        Sent to customer
                      </>
                    ) : (
                      <>
                        <EyeOff className="size-3" />
                        Internal
                      </>
                    )}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{u.message}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
